import useDiscoveryFetch from './useDiscoveryFetch';
import './cityDetail/CityDiscoveryListMobile.css';
import MobileJobsSortDrawer from './MobileJobsSortDrawer';
import { useState, useEffect, useMemo, useRef } from 'react';
import { Table, Form, Row, Col, Card, Overlay, Tooltip } from 'react-bootstrap';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Container from 'react-bootstrap/Container';
import Pagination from 'react-bootstrap/Pagination';
import { useMediaQuery } from 'react-responsive';
import { FaCaretUp, FaCaretDown } from 'react-icons/fa';
import { BsChevronLeft, BsChevronRight } from 'react-icons/bs';
import api from '../../api/axiosInstance';
import { trackEvent } from '../../services/analytics/analyticsService';
import { ANALYTICS_EVENTS } from '../../services/analytics/analyticsEvents';
import defaultLogo from '../../assets/img/ic-img.svg';
import defaultCompanyMarkerLogo from '../../assets/img/ic-building-company.svg';
import Sidebar from '../../Sidebar';
import Breadcrumb from '../../Breadcrumb';
import DiscoveryMap from './DiscoveryMap';
import DiscoveryFilterBar from './DiscoveryFilterBar';
import DiscoveryEmptyState from './DiscoveryEmptyState';
import MobileDiscoveryHeading from './MobileDiscoveryHeading';
import MobileDiscoverySearchToolbar from './MobileDiscoverySearchToolbar';
import MobileDiscoveryViewSwitch from './MobileDiscoveryViewSwitch';
import MobileCompanyCard from './MobileCompanyCard';
import MobileAppliedChips from './MobileAppliedChips';
import MobileLoadMoreFooter from './MobileLoadMoreFooter';
import useMobileLoadMore from './useMobileLoadMore';
import useMobileDiscoveryNavDocking from './useMobileDiscoveryNavDocking';
import { captureDiscoveryFilters, buildDiscoveryFilterChipLabels } from './discoveryMobileFilters';
import DiscoveryDesktopAiSlot from './DiscoveryDesktopAiSlot';
import NavTabs from '../../NavTabs';
import CityDetailHero from './cityDetail/CityDetailHero';
import CityDetailMobileTabs from './cityDetail/CityDetailMobileTabs';
import normalizeUrl from './cityDetail/normalizeUniversityUrl';
import './cityDetail/CityJobsMobile.css';
import CountryDetailMobileTabs from './countryDetail/CountryDetailMobileTabs';
import getCountryDetailNavItems from './countryDetail/countryDetailNavItems';
import icFilter from '../../assets/img/ic-filter.svg';
import icSort from '../../assets/img/ic-sort.svg';
import icJobSearch from '../../assets/img/ic-job-search.svg';
import icLink from '../../assets/img/ic-link-square.svg';
import icMail from '../../assets/img/ic-mail-send.svg';
import icPhone from '../../assets/img/ic-smart-phone.svg';
import icHeartEmpty from '../../assets/img/ic-heart-empty.svg';
import icHeartFull from '../../assets/img/ic-heart-full.svg';
import { useMobileAiPageContext } from '../../MobileAiAssistantContext';
import { getDiscoveryAiSuggestions } from '../../utils/discoveryAiSuggestions';

const PAGE_DISCOVERY_TYPE = 'jobs';

// CONTRACT: this mapping must stay identical to AI_INDUSTRY_GROUPS in the backend's
// discoveryAiContextService.js (and in DiscoveryCompanyList.js/DiscoveryJobsList.js/
// DiscoveryCityCompanyList.js). Update all together.
const AI_INDUSTRY_GROUPS = {
    'financial services': ['64', '65', '66'],
    'software': ['62', '63'],
};

const mobileJobSortDefinitions = {
    name: { defaultDirection: 'asc', value: (company) => String(company.title || '').toLowerCase(), type: 'text' },
    city: { defaultDirection: 'asc', value: (company) => String(company.city || company.cityRegion || '').toLowerCase(), type: 'text' },
    student: { defaultDirection: 'desc', value: (company) => Number(company.openJobsStudent) || 0, type: 'number' },
    intern: { defaultDirection: 'desc', value: (company) => Number(company.openJobsIntern) || 0, type: 'number' },
    oneToThree: { defaultDirection: 'desc', value: (company) => Number(company.openJobs13) || 0, type: 'number' },
    threePlus: { defaultDirection: 'desc', value: (company) => Number(company.openJobs3) || 0, type: 'number' },
    total: { defaultDirection: 'desc', value: (company) => Number(company.openJobsTotal) || 0, type: 'number' },
    lastUpdate: { defaultDirection: 'desc', value: (company) => new Date(company.latestDataUpdate || company.updatedAt || 0).getTime(), type: 'number' },
};

const getMobileSortedJobs = (sourceCompanies, field, direction) => {
    const definition = mobileJobSortDefinitions[field];
    if (!definition) return sourceCompanies;
    const multiplier = direction === 'asc' ? 1 : -1;
    return [...sourceCompanies].sort((a, b) => {
        const valueA = definition.value(a);
        const valueB = definition.value(b);
        const comparison = definition.type === 'text'
            ? valueA.localeCompare(valueB)
            : valueA - valueB;
        return comparison * multiplier;
    });
};

const DiscoveryCityJobsList = ({ countryScoped = false }) => {
    const navigate = useNavigate();
    const { id, cityId } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const actualCityId = countryScoped ? null : (cityId || id);
    const countryCode = countryScoped ? id : (cityId ? id : null);

    const [cityUrl, setCityUrl] = useState('');
    const [cityName, setCityName] = useState('');
    const [countryName, setCountryName] = useState('');
    // Keep the last explicitly applied comparator when fresh API data arrives.
    const refreshSortRef = useRef(null);
    const [companies, setCompanies] = useState([]);
    const [industriesData, setIndustriesData] = useState([]);
    const [favorites, setFavorites] = useState(new Set());

    const [sortOrderName, setSortOrderName] = useState('asc');
    const [sortOrderCity, setSortOrderCity] = useState('asc');
    const [sortOrderStudent, setSortOrderStudent] = useState('asc');
    const [sortOrderIntern, setSortOrderIntern] = useState('asc');
    const [sortOrder13, setSortOrder13] = useState('asc');
    const [sortOrder3, setSortOrder3] = useState('asc');
    const [sortOrderTotal, setSortOrderTotal] = useState('asc');
    const [sortOrderLastUpdate, setSortOrderLastUpdate] = useState('asc');

    const [currentPage, setCurrentPage] = useState(1);
    const [companiesPerPage, setCompaniesPerPage] = useState(10);

    // Initialized once from the URL; ?industry=Financial Services stays a group match
    // for the life of the page rather than a single selectedIndustry code.
    const [aiIndustryGroup, setAiIndustryGroup] = useState(() => {
        const rawParam = String(searchParams.get('industry') || '').trim();
        return AI_INDUSTRY_GROUPS[rawParam.toLowerCase()] ? rawParam : null;
    });

    const clearAiIndustryGroup = () => {
        setAiIndustryGroup(null);
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.delete('industry');
            return next;
        }, { replace: true });
    };

    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCity, setSelectedCity] = useState('');
    const [selectedIndustry, setSelectedIndustry] = useState('');
    const [selectedStudyDirection, setSelectedStudyDirection] = useState('');
    const [selectedCompanySize, setSelectedCompanySize] = useState('');
    const [selectedCompanyRating, setSelectedCompanyRating] = useState('');
    const [selectedEnglishSpeaking, setSelectedEnglishSpeaking] = useState(
        searchParams.get('englishSpeaking') === 'true' ? true : null
    );
    const [selectedWorldTraveler, setSelectedWorldTraveler] = useState(null);
    const [selectedStudentJobAreas, setSelectedStudentJobAreas] = useState(
        searchParams.get('studentFriendly') === 'true' ? true : null
    );
    const [selectedInternship, setSelectedInternship] = useState(
        searchParams.get('internship') === 'true' ? true : null
    );
    const [selectedFavorites, setSelectedFavorites] = useState(null);
    const [savedFilterSets, setSavedFilterSets] = useState([]);
    const [selectedFilterSet, setSelectedFilterSet] = useState('default');
    const [showMobileFilters, setShowMobileFilters] = useState(false);
    const [showMobileSort, setShowMobileSort] = useState(false);
    const [sortField, setSortField] = useState(null);
    const [sortDirection, setSortDirection] = useState(null);
    const [draftSortField, setDraftSortField] = useState(null);
    const [draftSortDirection, setDraftSortDirection] = useState(null);
    const [appliedMobileFilters, setAppliedMobileFilters] = useState(() => captureDiscoveryFilters({
        selectedCity,
        selectedIndustry,
        aiIndustryGroup,
        selectedStudyDirection,
        selectedCompanySize,
        selectedCompanyRating,
        selectedEnglishSpeaking,
        selectedWorldTraveler,
        selectedStudentJobAreas,
        selectedInternship,
        selectedFavorites,
    }));

    // Matches the mobile card/filter-modal breakpoint used across Discovery pages.
    const isMobile = useMediaQuery({ query: countryScoped ? '(max-width: 767.98px)' : '(max-width: 991.98px)' });
    const { isMobileNavDocked, mobileMapRowRef } = useMobileDiscoveryNavDocking();

    const [overlayTarget, setOverlayTarget] = useState(null);
    const [showCopied, setShowCopied] = useState(false);
    const copyTimeoutRef = useRef(null);
    const contactRefs = useRef({});

    const industryNameByCode = useMemo(() => {
        const map = new Map();
        (industriesData || []).forEach((industry) => {
            if (industry?.code) {
                map.set(String(industry.code), industry.name || String(industry.code));
            }
        });
        return map;
    }, [industriesData]);

    const currentFilterValues = captureDiscoveryFilters({
        selectedCity, selectedIndustry, aiIndustryGroup, selectedStudyDirection,
        selectedCompanySize, selectedCompanyRating, selectedEnglishSpeaking,
        selectedWorldTraveler, selectedStudentJobAreas, selectedInternship, selectedFavorites,
    });
    // City drawer edits remain staged until Apply, including Clear all.
    const effectiveFilters = !countryScoped && isMobile && showMobileFilters
        ? appliedMobileFilters : currentFilterValues;
    const filteredCompanies = companies.filter((company) => {
        const { selectedCity, selectedIndustry, aiIndustryGroup, selectedStudyDirection,
            selectedEnglishSpeaking, selectedWorldTraveler, selectedStudentJobAreas,
            selectedInternship, selectedFavorites } = effectiveFilters;
        const normalizeStudyDirection = (val) =>
            String(val).trim()
                .replace(/^\d+(?:\.\d+)?\s*[-–—]?\s*/, '')
                .replace(/([.:])\s*\d+(?:\.\d+)?\s*[-–—]?\s*/g, '$1 ')
                .replace(/^\(\s*/, '')
                .replace(/\)\s*$/, '')
                .replace(/\s{2,}/g, ' ')
                .trim()
                .toLowerCase();

        const matchesSearch =
            !searchQuery ||
            company.title?.toLowerCase().includes(searchQuery.toLowerCase());

        const matchesCity =
            !selectedCity ||
            String(company.city || '').trim() === String(selectedCity || '').trim();

        const aiIndustryGroupCodes = aiIndustryGroup
            ? AI_INDUSTRY_GROUPS[aiIndustryGroup.toLowerCase()]
            : null;

        const matchesIndustry = aiIndustryGroupCodes
            ? (Array.isArray(company.industries) &&
                company.industries.some((entry) =>
                    aiIndustryGroupCodes.includes(String(entry?.industry || '').trim())
                ))
            : (
                !selectedIndustry ||
                (Array.isArray(company.industries) &&
                    company.industries.some((entry) => entry?.industry === selectedIndustry))
            );

        const matchesStudyDirection =
            !selectedStudyDirection ||
            (() => {
                const rawStudyDirections = company.studyFields?.preferredStudyDirections;
                if (Array.isArray(rawStudyDirections)) {
                    return rawStudyDirections.some(
                        (direction) => normalizeStudyDirection(direction) === normalizeStudyDirection(selectedStudyDirection)
                    );
                }
                if (typeof rawStudyDirections === 'string') {
                    return rawStudyDirections
                        .split(',')
                        .map((direction) => normalizeStudyDirection(direction))
                        .includes(normalizeStudyDirection(selectedStudyDirection));
                }
                return false;
            })();

        const matchesEnglishSpeaking =
            selectedEnglishSpeaking === null || company.openedEnglishSpeaking === true;

        const matchesWorldTraveler =
            selectedWorldTraveler === null || company.openedWorldTraveler === true;

        const matchesStudentJobAreas =
            selectedStudentJobAreas === null ||
            (Array.isArray(company.studentJobAreas)
                ? company.studentJobAreas.length > 0
                : Boolean(company.studentJobAreas));

        const matchesInternship =
            selectedInternship === null || Number(company.openJobsIntern) > 0;

        const matchesFavorites =
            selectedFavorites === null || favorites.has(String(company._id));

        return (
            matchesSearch &&
            matchesCity &&
            matchesIndustry &&
            matchesStudyDirection &&
            matchesEnglishSpeaking &&
            matchesWorldTraveler &&
            matchesStudentJobAreas &&
            matchesInternship &&
            matchesFavorites
        );
    });

    const indexOfLastCompany = currentPage * companiesPerPage;
    const indexOfFirstCompany = indexOfLastCompany - companiesPerPage;
    const currentCompanies = filteredCompanies.slice(indexOfFirstCompany, indexOfLastCompany);
    const totalPages = Math.ceil(filteredCompanies.length / companiesPerPage);
    const noCompanies = filteredCompanies.length === 0;
    const hasActiveJobFilters = Boolean(
        searchQuery.trim() ||
        selectedCity ||
        selectedIndustry ||
        selectedStudyDirection ||
        selectedCompanySize ||
        selectedCompanyRating ||
        selectedEnglishSpeaking !== null ||
        selectedWorldTraveler !== null ||
        selectedStudentJobAreas !== null ||
        selectedInternship !== null ||
        selectedFavorites !== null ||
        Boolean(aiIndustryGroup)
    );
    const activeCityNames = useMemo(() => new Set(
        companies.map((company) => String(company.city || company.cityRegion || '').trim()).filter(Boolean)
    ), [companies]);
    const mobileSortedCompanies = sortField
        ? getMobileSortedJobs(filteredCompanies, sortField, sortDirection)
        : filteredCompanies;
    const mobileFilterKey = JSON.stringify({
        countryId: countryCode,
        cityId: actualCityId,
        searchQuery,
        ...effectiveFilters,
        sortField,
        sortDirection,
    });
    const mobilePagination = useMobileLoadMore({
        items: mobileSortedCompanies,
        filterKey: mobileFilterKey,
        storageKey: countryScoped ? `discovery-country-${countryCode}-jobs-mobile-list` : `discovery-city-${countryCode || "global"}-${actualCityId}-jobs-mobile-list`,
        isMobile,
    });

    useEffect(() => {
        setCurrentPage(1);
    }, [
        searchQuery,
        selectedCity,
        selectedIndustry,
        selectedStudyDirection,
        selectedCompanySize,
        selectedCompanyRating,
        selectedEnglishSpeaking,
        selectedWorldTraveler,
        selectedStudentJobAreas,
        selectedInternship,
        selectedFavorites,
    ]);

    const buildCurrentFiltersPayload = () => ({
        searchQuery,
        selectedCity,
        selectedIndustry,
        selectedStudyDirection,
        selectedCompanySize,
        selectedCompanyRating,
        selectedEnglishSpeaking,
        selectedWorldTraveler,
        selectedStudentJobAreas,
        selectedInternship,
        selectedFavorites,
    });

    const applySavedFilterSet = (filters = {}) => {
        setSearchQuery(filters.searchQuery || '');
        setSelectedCity(!countryScoped && isMobile ? '' : filters.selectedCity || '');
        setSelectedIndustry(filters.selectedIndustry || '');
        setSelectedStudyDirection(filters.selectedStudyDirection || '');
        setSelectedCompanySize(filters.selectedCompanySize || '');
        setSelectedCompanyRating(filters.selectedCompanyRating || '');
        setSelectedEnglishSpeaking(filters.selectedEnglishSpeaking === true ? true : null);
        setSelectedWorldTraveler(filters.selectedWorldTraveler === true ? true : null);
        setSelectedStudentJobAreas(filters.selectedStudentJobAreas === true ? true : null);
        setSelectedInternship(filters.selectedInternship === true ? true : null);
        setSelectedFavorites(filters.selectedFavorites === true ? true : null);
    };

    const handleSaveFilters = async (filterSetName) => {
        try {
            const trimmedName = String(filterSetName || '').trim();
            if (!trimmedName) return;
            const payload = {
                page: countryScoped ? 'country-jobs' : 'city-jobs',
                name: trimmedName,
                filters: buildCurrentFiltersPayload(),
            };
            const res = await api.post('/api/discovery-filter-sets', payload);
            const savedFilterSet = res.data;
            setSavedFilterSets((prev) => {
                const withoutDuplicate = prev.filter(
                    (item) => item._id !== savedFilterSet._id && item.name !== savedFilterSet.name
                );
                return [savedFilterSet, ...withoutDuplicate];
            });
            setSelectedFilterSet(savedFilterSet._id);
        } catch (error) {
            console.error('Error saving discovery city job filter set:', error);
        }
    };

    const handleDeleteFilterSet = async (filterSetId) => {
        if (!filterSetId || filterSetId === 'default') return;
        try {
            await api.delete(`/api/discovery-filter-sets/${filterSetId}`);
            const wasSelected = String(selectedFilterSet) === String(filterSetId);
            setSavedFilterSets((prev) =>
                prev.filter((filterSet) => String(filterSet._id) !== String(filterSetId))
            );
            if (wasSelected) {
                setSelectedFilterSet('default');
                applySavedFilterSet({});
            }
        } catch (err) {
            console.error('Error deleting filter set:', err);
        }
    };

    const handleFilterSetChange = (value) => {
        setSelectedFilterSet(value);
        if (value === 'default') {
            applySavedFilterSet({});
            return;
        }
        const selectedSavedFilterSet = savedFilterSets.find((item) => item._id === value);
        if (selectedSavedFilterSet?.filters) {
            applySavedFilterSet(selectedSavedFilterSet.filters);
        }
    };

    useDiscoveryFetch(({ signal, isCurrent }) => {
        const fetchData = async () => {
            let resolvedCityName = actualCityId;
            try {
                if (countryScoped) {
                    const countryRes = await api.get(`/api/discovery/countries/${countryCode}`, { signal });
                    if (!isCurrent()) return;
                    const countryData = countryRes.data;
                    setCountryName(countryData?.countryName || countryData?.country?.name || countryCode);
                } else {
                    const cityRes = await api.get(`/api/discovery/cities/${actualCityId}`, { signal });
                    if (!isCurrent()) return;
                    const cityData = cityRes.data;
                    resolvedCityName = cityData?.cityName || cityData?.name || actualCityId;
                    setCityName(resolvedCityName);
                    setCityUrl(cityData?.url || '');
                    setCountryName(cityData?.country?.name || '');
                }
            } catch (error) {
                if (!isCurrent()) return;
                console.error('Error fetching city data:', error);
            }

            try {
                const [jobsRes, industriesRes, filterSetsRes, currentUserRes] = await Promise.all([
                    api.get('/api/discovery/jobs', { signal }),
                    api.get('/api/nace', { signal }),
                    api.get(`/api/discovery-filter-sets?page=${countryScoped ? 'country-jobs' : 'city-jobs'}`, { signal }),
                    api.get('/api/users/me', { signal }),
                ]);
                if (!isCurrent()) return;

                const allJobs = Array.isArray(jobsRes.data) ? jobsRes.data : [];
                const normalizedScope = String(countryScoped ? countryCode : resolvedCityName || '').trim().toLowerCase();
                const cityJobs = allJobs
                    .filter((company) => String(
                        countryScoped ? company.country : company.cityRegion || ''
                    ).trim().toLowerCase() === normalizedScope)
                    .map(({ cityRegion, ...company }) => ({
                        ...company,
                        city: cityRegion || company.city,
                    }));
                const safeIndustries = Array.isArray(industriesRes.data) ? industriesRes.data : [];
                const safeFilterSets = Array.isArray(filterSetsRes.data) ? filterSetsRes.data : [];
                const safeFavoriteCompanies = Array.isArray(currentUserRes?.data?.favoriteCompanies)
                    ? currentUserRes.data.favoriteCompanies.map((companyId) => String(companyId))
                    : [];

                setCompanies(refreshSortRef.current ? [...cityJobs].sort(refreshSortRef.current) : cityJobs);
                setIndustriesData(safeIndustries);
                setSavedFilterSets(safeFilterSets);
                setFavorites(new Set(safeFavoriteCompanies));
            } catch (error) {
                if (!isCurrent()) return;
                console.error('Error fetching city jobs / industries / filter sets / favorites:', error);
            }
        };

        fetchData();
    }, [actualCityId, countryCode, countryScoped]);

    // Resolves an industry display label (from a URL ?industry= param or directly from
    // an AI chip's structuredFilters.industry) to what selectedIndustry/aiIndustryGroup
    // actually filter on: a known AI_INDUSTRY_GROUPS key stays a group match; anything
    // else is fuzzy-matched against the NACE list to a single code. No match -> no-op.
    const resolveIndustryLabel = (label) => {
        const trimmedLabel = String(label || '').trim();
        if (!trimmedLabel) return;

        const normalizedLabel = trimmedLabel.toLowerCase();

        if (AI_INDUSTRY_GROUPS[normalizedLabel]) {
            setAiIndustryGroup(trimmedLabel);
            return;
        }

        if (industriesData.length === 0) return;

        const matchedIndustry = industriesData.find((industry) => {
            const normalizedName = String(industry.name || '').trim().toLowerCase();
            return (
                normalizedName === normalizedLabel ||
                normalizedName.includes(normalizedLabel) ||
                normalizedLabel.includes(normalizedName)
            );
        });

        if (matchedIndustry) {
            setSelectedIndustry(String(matchedIndustry.code));
        }
    };

    // One-shot: resolve an ?industry= query param the same way. Only runs once
    // industriesData has loaded, and only ever applies once.
    const appliedIndustryParamRef = useRef(false);

    useEffect(() => {
        if (appliedIndustryParamRef.current) return;
        if (industriesData.length === 0) return;

        appliedIndustryParamRef.current = true;

        const industryParam = searchParams.get('industry');
        if (!industryParam) return;

        // Broad AI industry groups are already applied via the aiIndustryGroup
        // initializer above; only the single-code fuzzy match needs to run here.
        if (AI_INDUSTRY_GROUPS[industryParam.trim().toLowerCase()]) return;

        resolveIndustryLabel(industryParam);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [industriesData, searchParams]);

    // This page already supports every field the AI can suggest, so every chip
    // applies locally.
    const handleChipClick = (sf) => {
        if (!sf) return;

        if (sf.city) setSelectedCity(sf.city);
        if (sf.industry) resolveIndustryLabel(sf.industry);

        if (sf.jobType === 'english-speaking' || sf.language === 'English') {
            setSelectedEnglishSpeaking(true);
        }

        if (sf.jobType === 'student') setSelectedStudentJobAreas(true);
        if (sf.jobType === 'internship') setSelectedInternship(true);
    };

    // Opt this page into the shared mobile AI assistant (header trigger + bottom sheet).
    useMobileAiPageContext(
        {
            pageType: countryScoped ? 'discovery-country-jobs' : 'discovery-city-jobs',
            mapFirst: countryScoped,
            mapScope: countryScoped ? 'scoped' : undefined,
            discoveryType: PAGE_DISCOVERY_TYPE,
            cityId: countryScoped ? undefined : actualCityId,
            cityName: countryScoped ? undefined : cityName,
            countryName,
            activeTab: 'Jobs',
            suggestions: getDiscoveryAiSuggestions({
                discoveryType: PAGE_DISCOVERY_TYPE,
                selectedCity: countryScoped ? selectedCity : cityName,
                selectedCountry: countryName,
            }),
        },
        { onApplyStructuredFilters: handleChipClick }
    );

    const sortByName = () => {
        const newOrder = sortOrderName === 'asc' ? 'desc' : 'asc';
        refreshSortRef.current = (a, b) => {
            const nameA = String(a.title || '').toLowerCase();
            const nameB = String(b.title || '').toLowerCase();
            return newOrder === 'asc' ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
        };
        setCompanies((prev) => [...prev].sort(refreshSortRef.current));
        setSortOrderName(newOrder);
    };

    const sortByCity = () => {
        const newOrder = sortOrderCity === 'asc' ? 'desc' : 'asc';
        refreshSortRef.current = (a, b) => {
            const cityA = String(a.city || '').toLowerCase();
            const cityB = String(b.city || '').toLowerCase();
            return newOrder === 'asc' ? cityA.localeCompare(cityB) : cityB.localeCompare(cityA);
        };
        setCompanies((prev) => [...prev].sort(refreshSortRef.current));
        setSortOrderCity(newOrder);
    };

    const sortByStudent = () => {
        const newOrder = sortOrderStudent === 'asc' ? 'desc' : 'asc';
        refreshSortRef.current = (a, b) => {
            const aVal = Number(a.openJobsStudent) || 0;
            const bVal = Number(b.openJobsStudent) || 0;
            return newOrder === 'asc' ? aVal - bVal : bVal - aVal;
        };
        setCompanies((prev) => [...prev].sort(refreshSortRef.current));
        setSortOrderStudent(newOrder);
    };

    const sortByIntern = () => {
        const newOrder = sortOrderIntern === 'asc' ? 'desc' : 'asc';
        refreshSortRef.current = (a, b) => {
            const aVal = Number(a.openJobsIntern) || 0;
            const bVal = Number(b.openJobsIntern) || 0;
            return newOrder === 'asc' ? aVal - bVal : bVal - aVal;
        };
        setCompanies((prev) => [...prev].sort(refreshSortRef.current));
        setSortOrderIntern(newOrder);
    };

    const sortBy13 = () => {
        const newOrder = sortOrder13 === 'asc' ? 'desc' : 'asc';
        refreshSortRef.current = (a, b) => {
            const aVal = Number(a.openJobs13) || 0;
            const bVal = Number(b.openJobs13) || 0;
            return newOrder === 'asc' ? aVal - bVal : bVal - aVal;
        };
        setCompanies((prev) => [...prev].sort(refreshSortRef.current));
        setSortOrder13(newOrder);
    };

    const sortBy3 = () => {
        const newOrder = sortOrder3 === 'asc' ? 'desc' : 'asc';
        refreshSortRef.current = (a, b) => {
            const aVal = Number(a.openJobs3) || 0;
            const bVal = Number(b.openJobs3) || 0;
            return newOrder === 'asc' ? aVal - bVal : bVal - aVal;
        };
        setCompanies((prev) => [...prev].sort(refreshSortRef.current));
        setSortOrder3(newOrder);
    };

    const sortByTotal = () => {
        const newOrder = sortOrderTotal === 'asc' ? 'desc' : 'asc';
        refreshSortRef.current = (a, b) => {
            const totalA = Number(a.openJobsTotal) || 0;
            const totalB = Number(b.openJobsTotal) || 0;
            return newOrder === 'asc' ? totalA - totalB : totalB - totalA;
        };
        setCompanies((prev) => [...prev].sort(refreshSortRef.current));
        setSortOrderTotal(newOrder);
    };

    const sortByLastUpdate = () => {
        const newOrder = sortOrderLastUpdate === 'asc' ? 'desc' : 'asc';
        refreshSortRef.current = (a, b) => {
            const dateA = new Date(a.latestDataUpdate || a.updatedAt || 0).getTime();
            const dateB = new Date(b.latestDataUpdate || b.updatedAt || 0).getTime();
            return newOrder === 'asc' ? dateA - dateB : dateB - dateA;
        };
        setCompanies((prev) => [...prev].sort(refreshSortRef.current));
        setSortOrderLastUpdate(newOrder);
    };

    const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);

    const getPaginationItems = () => {
        const siblingCount = 2;
        const boundaryCount = 1;
        if (totalPages <= 1) return [1];
        const range = (start, end) => {
            const out = [];
            for (let i = start; i <= end; i++) out.push(i);
            return out;
        };
        const startPages = range(1, Math.min(boundaryCount, totalPages));
        const endPages = range(Math.max(totalPages - boundaryCount + 1, boundaryCount + 1), totalPages);
        const siblingsStart = Math.max(
            Math.min(currentPage - siblingCount, totalPages - boundaryCount - siblingCount * 2 - 1),
            boundaryCount + 2
        );
        const siblingsEnd = Math.min(
            Math.max(currentPage + siblingCount, boundaryCount + siblingCount * 2 + 2),
            totalPages - boundaryCount - 1
        );
        const items = [
            ...startPages,
            ...(siblingsStart > boundaryCount + 2 ? ['ellipsis'] : boundaryCount + 1 < totalPages - boundaryCount ? [boundaryCount + 1] : []),
            ...range(siblingsStart, siblingsEnd),
            ...(siblingsEnd < totalPages - boundaryCount - 1 ? ['ellipsis'] : totalPages - boundaryCount > boundaryCount ? [totalPages - boundaryCount] : []),
            ...endPages,
        ];
        return items.filter((v, i) => items.indexOf(v) === i);
    };

    const toggleFavorite = async (companyId) => {
        const companyIdStr = String(companyId);
        const previousFavorites = new Set(favorites);
        const wasFavorited = previousFavorites.has(companyIdStr);
        const optimisticFavorites = new Set(previousFavorites);
        if (wasFavorited) {
            optimisticFavorites.delete(companyIdStr);
        } else {
            optimisticFavorites.add(companyIdStr);
        }
        setFavorites(optimisticFavorites);
        try {
            const res = await api.patch(`/api/users/me/favorite-companies/${companyIdStr}`);
            const safeFavoriteCompanies = Array.isArray(res?.data?.favoriteCompanies)
                ? res.data.favoriteCompanies.map((favId) => String(favId))
                : [];
            setFavorites(new Set(safeFavoriteCompanies));

            const company = companies.find((c) => String(c._id) === companyIdStr);
            trackEvent(
                wasFavorited ? ANALYTICS_EVENTS.FAVORITE_COMPANY_REMOVED : ANALYTICS_EVENTS.FAVORITE_COMPANY_ADDED,
                {
                    companyId: companyIdStr,
                    companyName: company?.title || '',
                    level: 3,
                    source: 'city_jobs_list',
                }
            );
        } catch (error) {
            console.error('Error toggling favorite company:', error);
            setFavorites(previousFavorites);
        }
    };

    const handleCopy = async (text) => {
        if (!text) return;
        try {
            await navigator.clipboard.writeText(text);
            setShowCopied(false);
            if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
            requestAnimationFrame(() => {
                setShowCopied(true);
                copyTimeoutRef.current = setTimeout(() => {
                    setShowCopied(false);
                    copyTimeoutRef.current = null;
                }, 1200);
            });
        } catch (err) {
            console.error('Copy failed:', err);
        }
    };

    const renderJobCount = (studentValue, companyValue = 0) => {
        const student = Number(studentValue) || 0;
        const company = Number(companyValue) || 0;
        if (!student && !company) return <span className="text-muted">n/a</span>;
        if (company) {
            return (
                <span className="fw-bold">
                    {company} <span className="text-muted fw-normal">({student})</span>
                </span>
            );
        }
        return <span className="text-primary fw-bold">{student}</span>;
    };

    const captureCurrentMobileFilters = () => captureDiscoveryFilters({
        selectedCity,
        selectedIndustry,
        aiIndustryGroup,
        selectedStudyDirection,
        selectedCompanySize,
        selectedCompanyRating,
        selectedEnglishSpeaking,
        selectedWorldTraveler,
        selectedStudentJobAreas,
        selectedInternship,
        selectedFavorites,
    });
    const restoreMobileFilters = (filters) => {
        setSelectedCity(filters.selectedCity);
        setSelectedIndustry(filters.selectedIndustry);
        setAiIndustryGroup(filters.aiIndustryGroup);
        setSelectedStudyDirection(filters.selectedStudyDirection);
        setSelectedCompanySize(filters.selectedCompanySize);
        setSelectedCompanyRating(filters.selectedCompanyRating);
        setSelectedEnglishSpeaking(filters.selectedEnglishSpeaking);
        setSelectedWorldTraveler(filters.selectedWorldTraveler);
        setSelectedStudentJobAreas(filters.selectedStudentJobAreas);
        setSelectedInternship(filters.selectedInternship);
        setSelectedFavorites(filters.selectedFavorites);
    };
    const closeMobileFilters = () => {
        restoreMobileFilters(appliedMobileFilters);
        setShowMobileFilters(false);
    };
    const openMobileFilters = () => {
        if (!countryScoped) setAppliedMobileFilters(captureCurrentMobileFilters());
        setShowMobileFilters(true);
    };
    const applyMobileFilters = () => {
        if (!countryScoped && appliedMobileFilters.aiIndustryGroup && !aiIndustryGroup) clearAiIndustryGroup();
        setAppliedMobileFilters(captureCurrentMobileFilters());
        setShowMobileFilters(false);
    };
    const clearMobileFiltersPreserveSearch = () => {
        const cleared = captureDiscoveryFilters({});
        restoreMobileFilters(cleared);
        if (countryScoped || !showMobileFilters) {
            setAppliedMobileFilters(cleared);
            clearAiIndustryGroup();
        }
    };
    const clearEmptyStateSearchAndFilters = () => {
        clearMobileFiltersPreserveSearch();
        setSearchQuery('');
    };
    const removeAppliedMobileFilter = (key) => {
        const booleanKeys = [
            'selectedEnglishSpeaking',
            'selectedWorldTraveler',
            'selectedStudentJobAreas',
            'selectedInternship',
            'selectedFavorites',
        ];
        const next = {
            ...(countryScoped ? appliedMobileFilters : currentFilterValues),
            [key]: booleanKeys.includes(key) || key === 'aiIndustryGroup' ? null : '',
        };
        restoreMobileFilters(next);
        setAppliedMobileFilters(next);
    };
    const mobileSortOptions = [
        { field: 'name', label: 'Company' },
        ...(countryScoped ? [{ field: 'city', label: 'City' }] : []),
        { field: 'student', label: 'Student jobs' },
        { field: 'intern', label: 'Internships' },
        { field: 'oneToThree', label: '1-3 years' },
        { field: 'threePlus', label: '3+ years' },
        { field: 'total', label: 'Total jobs' },
        { field: 'lastUpdate', label: 'Last update' },
    ];
    const activeMobileSortOption = mobileSortOptions.find((option) => option.field === sortField);
    const openMobileSort = () => {
        setDraftSortField(sortField);
        setDraftSortDirection(sortDirection);
        setShowMobileSort(true);
    };
    const closeMobileSort = () => {
        setDraftSortField(sortField);
        setDraftSortDirection(sortDirection);
        setShowMobileSort(false);
    };
    const clearMobileSort = () => {
        setSortField(null);
        setSortDirection(null);
    };
    const clearMobileSortFromDrawer = () => {
        clearMobileSort();
        setDraftSortField(null);
        setDraftSortDirection(null);
        setShowMobileSort(false);
    };
    const selectDraftSort = (field) => {
        setDraftSortDirection((direction) => (
            draftSortField === field
                ? (direction === 'asc' ? 'desc' : 'asc')
                : mobileJobSortDefinitions[field].defaultDirection
        ));
        setDraftSortField(field);
    };
    const applyMobileSort = () => {
        setSortField(draftSortField);
        setSortDirection(draftSortDirection);
        setShowMobileSort(false);
    };
    const appliedMobileChips = buildDiscoveryFilterChipLabels(
        !countryScoped && !showMobileFilters ? currentFilterValues : appliedMobileFilters,
        industryNameByCode
    )
        .filter((chip) => chip.key !== 'selectedCountry')
        .map((chip) => ({ ...chip, onRemove: () => removeAppliedMobileFilter(chip.key) }));
    if (activeMobileSortOption) {
        appliedMobileChips.push({
            key: 'activeSort',
            label: activeMobileSortOption.label,
            icon: sortDirection === 'asc' ? <FaCaretUp aria-hidden="true" /> : <FaCaretDown aria-hidden="true" />,
            onOpen: openMobileSort,
            onRemove: clearMobileSort,
            removeLabel: `Clear ${activeMobileSortOption.label} sort`,
        });
    }

    const companiesWithCoordinates = filteredCompanies.filter((company) => {
        const lat = Number(company.latitude);
        const lng = Number(company.longitude);
        return !Number.isNaN(lat) && !Number.isNaN(lng);
    });

    const anyCompaniesWithCoordinates = companies.some((company) => {
        const lat = Number(company.latitude);
        const lng = Number(company.longitude);
        return !Number.isNaN(lat) && !Number.isNaN(lng);
    });

    const basePath = countryScoped
        ? `/discovery/countries/${countryCode}`
        : countryCode
            ? `/discovery/countries/${countryCode}/cities/${actualCityId}`
            : `/discovery/cities/${actualCityId}`;
    const companiesPath = `${basePath}/companies`;
    const jobsPath = countryScoped ? `${basePath}/jobs` : `${basePath}/companies/jobs`;
    const cityNavItems = [
        { to: basePath, eventKey: 'about', label: 'General Info', end: true },
        { to: `${basePath}/jobs`, eventKey: 'companies', label: 'Companies & Jobs', alsoActive: [`${basePath}/companies`, `${basePath}/companies/jobs`] },
        { to: `${basePath}/salary`, eventKey: 'salary', label: 'Average salaries' },
        { to: `${basePath}/insights`, eventKey: 'insights', label: 'City insights' },
        { to: `${basePath}/universities`, eventKey: 'universities', label: 'Universities' },
        { to: `${basePath}/comments`, eventKey: 'comments', label: 'Comments' },
    ];
    const detailNavItems = countryScoped ? getCountryDetailNavItems(countryCode) : cityNavItems;

    const mobileToolbar = (
        <>
            {countryScoped && <MobileDiscoveryViewSwitch
                activeView="jobs"
                routes={{ companies: `${basePath}/companies`, jobs: `${basePath}/jobs` }}
            />}

            {countryScoped && <MobileDiscoveryHeading title="Jobs" />}

            <MobileDiscoverySearchToolbar
                searchValue={searchQuery}
                onSearchChange={setSearchQuery}
                searchPlaceholder="Search..."
                searchAriaLabel="Search jobs"
                actions={[
                    { label: 'Open filters', icon: icFilter, onClick: openMobileFilters },
                    { label: 'Sort jobs', icon: icSort, onClick: openMobileSort },
                ]}
            />

            <MobileAppliedChips chips={appliedMobileChips} />

            <DiscoveryFilterBar
                title="Jobs"
                items={companies}
                allowedCities={activeCityNames}
                industryNameByCode={industryNameByCode}
                showCountryFilter={false}
                showCityFilter={countryScoped}
                cityField="city"
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                selectedCity={selectedCity}
                setSelectedCity={setSelectedCity}
                selectedIndustry={selectedIndustry}
                setSelectedIndustry={setSelectedIndustry}
                selectedStudyDirection={selectedStudyDirection}
                setSelectedStudyDirection={setSelectedStudyDirection}
                selectedCompanySize={selectedCompanySize}
                setSelectedCompanySize={setSelectedCompanySize}
                selectedCompanyRating={selectedCompanyRating}
                setSelectedCompanyRating={setSelectedCompanyRating}
                selectedEnglishSpeaking={selectedEnglishSpeaking}
                setSelectedEnglishSpeaking={setSelectedEnglishSpeaking}
                selectedWorldTraveler={selectedWorldTraveler}
                setSelectedWorldTraveler={setSelectedWorldTraveler}
                selectedStudentJobAreas={selectedStudentJobAreas}
                setSelectedStudentJobAreas={setSelectedStudentJobAreas}
                aiIndustryGroupLabel={aiIndustryGroup}
                onClearAiIndustryGroup={countryScoped ? clearAiIndustryGroup : () => setAiIndustryGroup(null)}
                selectedFavorites={selectedFavorites}
                setSelectedFavorites={setSelectedFavorites}
                savedFilterSets={savedFilterSets}
                selectedFilterSet={selectedFilterSet}
                onFilterSetChange={handleFilterSetChange}
                onDeleteFilterSet={handleDeleteFilterSet}
                onSaveFilters={handleSaveFilters}
                onClearFilters={clearMobileFiltersPreserveSearch}
                mobileModalOnly
                mobileModalOpen={showMobileFilters}
                onMobileModalClose={closeMobileFilters}
                onMobileModalApply={applyMobileFilters}
                mobileModalTitle="Filter jobs"
                mobileApplyLabel="Apply filters"
                hideTypeToggleOnMobile
                hideActiveFilterSummaryOnMobile
                useFullScreenMobilePanel
                mobileSavedFilterLabel="Saved filter"
                mobileSaveFilterLabel="Save current filters"
                compactSavedFilterOnMobile
                hideSearchInMobileModal
            />
        </>
    );
    const mobileResults = (
        noCompanies ? (
            <DiscoveryEmptyState
                className="discovery-empty-state--mobile-jobs"
                icon={icJobSearch}
                title={hasActiveJobFilters ? 'No matching jobs found' : 'No jobs available'}
                description={hasActiveJobFilters
                    ? 'Try removing one or more filters or adjust your search.'
                    : 'There are currently no jobs available for this selection. You can explore companies instead.'}
                primaryAction={hasActiveJobFilters ? {
                    label: 'Clear search & filters',
                    variant: 'primary',
                    onClick: clearEmptyStateSearchAndFilters,
                } : {
                    label: 'View companies',
                    variant: 'primary',
                    onClick: () => navigate(companiesPath),
                }}
                secondaryAction={hasActiveJobFilters ? {
                    label: 'Adjust filters',
                    variant: 'outline-secondary',
                    onClick: openMobileFilters,
                } : null}
            />
        ) : (
            <div className="mobile-company-list mobile-company-list--country-detail">
                {mobilePagination.mobileItems.map((company) => (
                    <MobileCompanyCard
                        key={company._id}
                        company={company}
                        hideCity={!countryScoped}
                        isFavorite={favorites.has(String(company._id))}
                        onFavoriteToggle={toggleFavorite}
                        onOpenCompany={(selectedCompany) => {
                            mobilePagination.rememberScrollPosition();
                            trackEvent(ANALYTICS_EVENTS.COMPANY_OPENED, {
                                companyId: selectedCompany._id,
                                companyName: selectedCompany.title,
                                source: countryScoped ? 'country_jobs_list' : 'city_jobs_list',
                                level: 3,
                                viewMode: 'jobs',
                            });
                            navigate(`${companiesPath}/${selectedCompany._id}/jobs`);
                        }}
                    />
                ))}
                <MobileLoadMoreFooter
                    visibleCount={mobilePagination.visibleCount}
                    totalCount={mobilePagination.totalCount}
                    itemLabel="jobs"
                    onLoadMore={mobilePagination.loadMore}
                    isLoading={mobilePagination.isLoadingMore}
                    hasMore={mobilePagination.hasMore}
                />
            </div>
        )
    );

    return (
        <>
            <Sidebar />
            <main className={`main-content flex-grow-1${!countryScoped && isMobile ? '' : ' discovery-map-page'}${countryScoped && isMobile ? ' discovery-jobs-page' : ''}${countryScoped && isMobileNavDocked ? ' is-mobile-nav-docked' : ''}`}>
                <Container>
                    <Row>
                        <Col>
                            {countryScoped ? (
                                <Breadcrumb countryName={countryName} subPage="Jobs" />
                            ) : (
                                <Breadcrumb cityName={cityName} subPage="Jobs" fromCountry={countryCode} countryName={countryName || undefined} />
                            )}
                        </Col>
                    </Row>

                    {!countryScoped && isMobile ? (
                        <Row className="g-0 country-detail-mobile city-detail-mobile">
                            <Col className="p-0">
                                <CityDetailHero cityName={cityName} countryName={countryName}
                                    url={normalizeUrl(cityUrl) ? { href: normalizeUrl(cityUrl), label: cityUrl } : null} />
                                <CityDetailMobileTabs items={detailNavItems} />
                                <section className="city-discovery-list-content city-jobs-section">
                                    <MobileDiscoveryViewSwitch activeView="jobs"
                                        routes={{ companies: `${basePath}/companies`, jobs: `${basePath}/jobs` }} />
                                    <MobileDiscoveryHeading title="Jobs" />
                                    {mobileToolbar}
                                    {mobileResults}
                                </section>
                            </Col>
                        </Row>
                    ) : (
                    <Row className="mb-4">
                        <Col>
                            <Card className="discovery-shell-card">
                                <Card.Body>
                                    <Row className="discovery-page-title-row">
                                        <Col xs={12}>
                                            <h1>{(countryScoped ? countryName : cityName) || 'Jobs'}</h1>
                                        </Col>
                                    </Row>

                                    {anyCompaniesWithCoordinates && (
                                        <Row ref={countryScoped ? mobileMapRowRef : undefined} className="discovery-map-row">
                                            <Col xs={12} className="px-0">
                                                <div className="discovery-mobile-map-area">
                                                    <DiscoveryMap
                                                        items={companiesWithCoordinates}
                                                        height={260}
                                                        markerScale={0.6}
                                                        clusterPixelRadius={20}
                                                        maxClusterZoom={9}
                                                        groupClickZoom={14}
                                                        canCluster={(a, b) => a.country === b.country}
                                                        getLatitude={(company) => company.latitude}
                                                        getLongitude={(company) => company.longitude}
                                                        getTitle={(company) => company.title}
                                                        getLogo={(company) => company.logo || defaultCompanyMarkerLogo}
                                                        getLink={(company) => `${companiesPath}/${company._id}/jobs`}
                                                        onLinkClick={(company) => trackEvent(ANALYTICS_EVENTS.COMPANY_OPENED, {
                                                            companyId: company._id,
                                                            companyName: company.title,
                                                            source: countryScoped ? 'country_jobs_map' : 'city_jobs_map',
                                                            level: 3,
                                                            viewMode: 'jobs',
                                                        })}
                                                    />
                                                </div>
                                            </Col>
                                        </Row>
                                    )}

                                    {countryScoped && isMobile ? (
                                        <CountryDetailMobileTabs items={detailNavItems} mapPage />
                                    ) : (
                                        <Row className="mb-4">
                                            <Col>
                                                <NavTabs items={detailNavItems} />
                                            </Col>
                                        </Row>
                                    )}

                                    <DiscoveryDesktopAiSlot />

                                    {!isMobile && (
                                        <Row className="mb-3">
                                            <Col xs={12}>
                                                <DiscoveryFilterBar
                                                    title="Jobs"
                                                    viewOptions={[
                                                        { value: 'companies', label: 'Companies' },
                                                        { value: 'jobs', label: 'Jobs' },
                                                    ]}
                                                    activeView="jobs"
                                                    onViewChange={(view) => {
                                                        if (view === 'companies') navigate(companiesPath);
                                                        if (view === 'jobs') navigate(jobsPath);
                                                    }}
                                                    items={companies}
                                                    industryNameByCode={industryNameByCode}
                                                    showCountryFilter={false}
                                                    showCityFilter={countryScoped}
                                                    cityField="city"
                                                    searchQuery={searchQuery}
                                                    setSearchQuery={setSearchQuery}
                                                    selectedCity={selectedCity}
                                                    setSelectedCity={setSelectedCity}
                                                    selectedIndustry={selectedIndustry}
                                                    setSelectedIndustry={setSelectedIndustry}
                                                    selectedStudyDirection={selectedStudyDirection}
                                                    setSelectedStudyDirection={setSelectedStudyDirection}
                                                    selectedCompanySize={selectedCompanySize}
                                                    setSelectedCompanySize={setSelectedCompanySize}
                                                    selectedCompanyRating={selectedCompanyRating}
                                                    setSelectedCompanyRating={setSelectedCompanyRating}
                                                    selectedEnglishSpeaking={selectedEnglishSpeaking}
                                                    setSelectedEnglishSpeaking={setSelectedEnglishSpeaking}
                                                    selectedWorldTraveler={selectedWorldTraveler}
                                                    setSelectedWorldTraveler={setSelectedWorldTraveler}
                                                    selectedStudentJobAreas={selectedStudentJobAreas}
                                                    setSelectedStudentJobAreas={setSelectedStudentJobAreas}
                                                    aiIndustryGroupLabel={aiIndustryGroup}
                                                    onClearAiIndustryGroup={clearAiIndustryGroup}
                                                    selectedFavorites={selectedFavorites}
                                                    setSelectedFavorites={setSelectedFavorites}
                                                    savedFilterSets={savedFilterSets}
                                                    selectedFilterSet={selectedFilterSet}
                                                    onFilterSetChange={handleFilterSetChange}
                                                    onDeleteFilterSet={handleDeleteFilterSet}
                                                    onSaveFilters={handleSaveFilters}
                                                />
                                            </Col>
                                        </Row>
                                    )}

                                    {isMobile && mobileToolbar}

                                    {isMobile ? mobileResults : noCompanies ? (
                                        <Row>
                                            <Col md={12}>
                                                <p className="mb-0">No jobs available.</p>
                                            </Col>
                                        </Row>
                                    ) : (
                                        <Row>
                                            <Col>
                                                <div className="table-responsive list">
                                                    <Table className="discovery-table">
                                                        <thead>
                                                            <tr>
                                                                <th onClick={sortByName} style={{ cursor: 'pointer' }}>
                                                                    <div>
                                                                        Company
                                                                        <span><FaCaretUp /><FaCaretDown /></span>
                                                                    </div>
                                                                </th>
                                                                {countryScoped && (
                                                                    <th onClick={sortByCity} style={{ cursor: 'pointer' }}>
                                                                        <div>
                                                                            City
                                                                            <span><FaCaretUp /><FaCaretDown /></span>
                                                                        </div>
                                                                    </th>
                                                                )}
                                                                <th>
                                                                    <div>HR contact</div>
                                                                </th>
                                                                <th>
                                                                    <div>Recruiting portal</div>
                                                                </th>
                                                                <th onClick={sortByStudent} style={{ cursor: 'pointer' }}>
                                                                    <div>
                                                                        Student jobs
                                                                        <span><FaCaretUp /><FaCaretDown /></span>
                                                                    </div>
                                                                </th>
                                                                <th onClick={sortByIntern} style={{ cursor: 'pointer' }}>
                                                                    <div>
                                                                        Intern
                                                                        <span><FaCaretUp /><FaCaretDown /></span>
                                                                    </div>
                                                                </th>
                                                                <th onClick={sortBy13} style={{ cursor: 'pointer' }}>
                                                                    <div>
                                                                        1–3 year
                                                                        <span><FaCaretUp /><FaCaretDown /></span>
                                                                    </div>
                                                                </th>
                                                                <th onClick={sortBy3} style={{ cursor: 'pointer' }}>
                                                                    <div>
                                                                        3+ year
                                                                        <span><FaCaretUp /><FaCaretDown /></span>
                                                                    </div>
                                                                </th>
                                                                <th onClick={sortByTotal} style={{ cursor: 'pointer' }}>
                                                                    <div>
                                                                        Total
                                                                        <span><FaCaretUp /><FaCaretDown /></span>
                                                                    </div>
                                                                </th>
                                                                <th onClick={sortByLastUpdate} style={{ cursor: 'pointer' }}>
                                                                    <div>
                                                                        Last Update
                                                                        <span><FaCaretUp /><FaCaretDown /></span>
                                                                    </div>
                                                                </th>
                                                                <th>Fav</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {currentCompanies.map((company) => (
                                                                <tr
                                                                    className="company-row"
                                                                    key={company._id}
                                                                    style={{ cursor: 'pointer' }}
                                                                    onClick={() => {
                                                                        trackEvent(ANALYTICS_EVENTS.COMPANY_OPENED, {
                                                                            companyId: company._id,
                                                                            companyName: company.title,
                                                                            source: countryScoped ? 'country_jobs_list' : 'city_jobs_list',
                                                                            level: 3,
                                                                            viewMode: 'jobs',
                                                                        });
                                                                        navigate(`${companiesPath}/${company._id}/jobs`);
                                                                    }}
                                                                >
                                                                    <td>
                                                                        <div>
                                                                            <img
                                                                                src={company.logo ? company.logo : defaultLogo}
                                                                                alt={company.title}
                                                                                className="round"
                                                                            />
                                                                            <span className="text-ellipsis text-ellipsis-country-name">
                                                                                <strong>{company.title}</strong>
                                                                            </span>
                                                                        </div>
                                                                    </td>
                                                                    {countryScoped && (
                                                                        <td>{company.city || <span className="text-muted">n/a</span>}</td>
                                                                    )}
                                                                    <td>
                                                                        <div className="d-inline-flex align-items-center gap-2">
                                                                            {company.hrEmail ? (
                                                                                <span
                                                                                    ref={(el) => { contactRefs.current[`${company._id}-email`] = el; }}
                                                                                    className="d-inline-flex align-items-center"
                                                                                    style={{ cursor: 'pointer' }}
                                                                                    onMouseEnter={() => setOverlayTarget({ el: contactRefs.current[`${company._id}-email`], key: `${company._id}-email` })}
                                                                                    onMouseLeave={() => { if (!showCopied) setOverlayTarget(null); }}
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        setOverlayTarget({ el: contactRefs.current[`${company._id}-email`], key: `${company._id}-email` });
                                                                                        handleCopy(company.hrEmail);
                                                                                    }}
                                                                                >
                                                                                    <img src={icMail} alt="email" style={{ width: '16px', height: '16px' }} />
                                                                                </span>
                                                                            ) : null}
                                                                            {company.hrPhone ? (
                                                                                <span
                                                                                    ref={(el) => { contactRefs.current[`${company._id}-phone`] = el; }}
                                                                                    className="d-inline-flex align-items-center"
                                                                                    style={{ cursor: 'pointer' }}
                                                                                    onMouseEnter={() => setOverlayTarget({ el: contactRefs.current[`${company._id}-phone`], key: `${company._id}-phone` })}
                                                                                    onMouseLeave={() => { if (!showCopied) setOverlayTarget(null); }}
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        setOverlayTarget({ el: contactRefs.current[`${company._id}-phone`], key: `${company._id}-phone` });
                                                                                        handleCopy(String(company.hrPhone));
                                                                                    }}
                                                                                >
                                                                                    <img src={icPhone} alt="phone" style={{ width: '16px', height: '16px' }} />
                                                                                </span>
                                                                            ) : null}
                                                                            {!company.hrEmail && !company.hrPhone ? <span className="text-muted">n/a</span> : null}
                                                                        </div>
                                                                    </td>
                                                                    <td>
                                                                        {company.hrRecrutingUrl ? (
                                                                            <a
                                                                                href={company.hrRecrutingUrl.startsWith('http') ? company.hrRecrutingUrl : `https://${company.hrRecrutingUrl}`}
                                                                                target="_blank"
                                                                                rel="noopener noreferrer"
                                                                                className="d-inline-flex align-items-center"
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    trackEvent(ANALYTICS_EVENTS.COMPANY_WEBSITE_CLICKED, {
                                                                                        companyId: company._id,
                                                                                        companyName: company.title,
                                                                                        urlType: 'recruiting_portal',
                                                                                        level: 3,
                                                                                        source: 'city_jobs_list',
                                                                                    });
                                                                                }}
                                                                            >
                                                                                <img src={icLink} alt="recruiting portal" style={{ width: '16px', height: '16px' }} />
                                                                            </a>
                                                                        ) : (
                                                                            <span className="text-muted">n/a</span>
                                                                        )}
                                                                    </td>
                                                                    <td>{renderJobCount(company.openJobsStudent)}</td>
                                                                    <td>{renderJobCount(company.openJobsIntern)}</td>
                                                                    <td>{renderJobCount(company.openJobs13)}</td>
                                                                    <td>{renderJobCount(company.openJobs3)}</td>
                                                                    <td>{renderJobCount(company.openJobsTotal)}</td>
                                                                    <td>
                                                                        <span className="text-muted small">
                                                                            {company.latestDataUpdate || company.updatedAt
                                                                                ? new Date(company.latestDataUpdate || company.updatedAt).toLocaleDateString()
                                                                                : '—'}
                                                                        </span>
                                                                    </td>
                                                                    <td>
                                                                        <span
                                                                            style={{ cursor: 'pointer' }}
                                                                            className="d-inline-flex align-items-center"
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                toggleFavorite(company._id);
                                                                            }}
                                                                        >
                                                                            <img
                                                                                src={favorites.has(String(company._id)) ? icHeartFull : icHeartEmpty}
                                                                                alt="favorite"
                                                                                style={{ width: '16px', height: '16px' }}
                                                                            />
                                                                        </span>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </Table>
                                                </div>
                                            </Col>
                                        </Row>
                                    )}

                                    {!isMobile && <Row className="pagination-wrapper">
                                        <Col className="d-flex align-items-center">
                                            <span>
                                                Total {filteredCompanies.length} {filteredCompanies.length === 1 ? 'company' : 'companies'}
                                            </span>
                                            <Pagination>
                                                <Pagination.Prev
                                                    onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                                                    disabled={currentPage === 1}
                                                >
                                                    <BsChevronLeft />
                                                </Pagination.Prev>
                                                {getPaginationItems().map((item, index) =>
                                                    item === 'ellipsis' ? (
                                                        <Pagination.Ellipsis key={`e-${index}`} disabled />
                                                    ) : (
                                                        <Pagination.Item
                                                            key={item}
                                                            active={item === currentPage}
                                                            onClick={() => handlePageChange(item)}
                                                        >
                                                            {item}
                                                        </Pagination.Item>
                                                    )
                                                )}
                                                <Pagination.Next
                                                    onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                                                    disabled={currentPage === totalPages || totalPages === 0}
                                                >
                                                    <BsChevronRight />
                                                </Pagination.Next>
                                            </Pagination>
                                        </Col>

                                        <Col className="pagination-controls">
                                            <div className="pagination-controls-per-page">
                                                <Form.Select
                                                    value={companiesPerPage}
                                                    onChange={(e) => {
                                                        setCompaniesPerPage(Number(e.target.value));
                                                        setCurrentPage(1);
                                                    }}
                                                >
                                                    <option value="10">10 / page</option>
                                                    <option value="20">20 / page</option>
                                                    <option value="40">40 / page</option>
                                                </Form.Select>
                                            </div>

                                            <label className="pagination-controls-goto-label" htmlFor="goto-page-city-jobs">
                                                Go to
                                            </label>

                                            <Form.Control
                                                id="goto-page-city-jobs"
                                                className="pagination-controls-goto-input"
                                                type="number"
                                                value={currentPage}
                                                onChange={(e) => {
                                                    const newPage = Number(e.target.value);
                                                    if (newPage >= 1 && newPage <= totalPages) {
                                                        setCurrentPage(newPage);
                                                    }
                                                }}
                                                onBlur={() => {
                                                    if (currentPage < 1) setCurrentPage(1);
                                                    if (currentPage > totalPages) setCurrentPage(totalPages || 1);
                                                }}
                                            />
                                        </Col>
                                    </Row>}
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                    )}
                </Container>
            </main>

            {(countryScoped || isMobile) && (
                <MobileJobsSortDrawer
                showMobileSort={showMobileSort}
                closeMobileSort={closeMobileSort}
                mobileSortOptions={mobileSortOptions}
                draftSortField={draftSortField}
                draftSortDirection={draftSortDirection}
                selectDraftSort={selectDraftSort}
                clearMobileSortFromDrawer={clearMobileSortFromDrawer}
                applyMobileSort={applyMobileSort}
            />
            )}

            <Overlay
                target={overlayTarget?.el}
                show={!!overlayTarget}
                placement="top"
                container={document.body}
                popperConfig={{ strategy: 'fixed' }}
            >
                {(props) => (
                    <Tooltip id="contact-copy-tooltip" {...props}>
                        {showCopied ? 'Copied!' : (
                            overlayTarget?.key?.endsWith('-email') ? 'Copy email' : 'Copy phone'
                        )}
                    </Tooltip>
                )}
            </Overlay>
        </>
    );
};

export default DiscoveryCityJobsList;
